import moment, { Moment } from 'moment-timezone';
import { ComparisonType } from '../entities';

/**
 * Get the current timestamp based on the timezone.
 *
 * If this is being called in Cypress we will return the date as 20th Feb 2020
 * so tests can remain consistent
 */
export const timeNow = (timezone?: string) => (timezone ? moment().tz(timezone) : moment());

/**
 * Gets the number of days between two dates and the number of days before/after now the range is.
 */
export const getDateRange = (
  start: Moment,
  end: Moment,
  now: Moment,
): { dateRange: number; dateRangeOffset: number } => {
  if (!start || !end || !now) {
    return {
      dateRange: 0,
      dateRangeOffset: 0,
    };
  }

  const isToday = start.isSame(now, 'day') && start.isSame(end, 'day');

  if (isToday) {
    return {
      dateRange: 0,
      dateRangeOffset: 0,
    };
  }

  const startDate = start.clone();
  const endDate = end.clone().add(1, 'day');

  const dateRange = endDate.startOf('day').diff(startDate.startOf('day'), 'days');
  const dateRangeOffset = now.startOf('day').diff(endDate.startOf('day'), 'days');
  return { dateRange, dateRangeOffset };
};

/**
 * When given a `dateRange` and `dateOffset` get a `startDate` and `endDate`.
 */
export const getStartAndEndDates = (dateRange: number, dateRangeOffset: number, time: Moment) => {
  const endDate = time
    .clone()
    .subtract(dateRangeOffset, 'days')
    .subtract(dateRange === 0 ? 0 : 1, 'days')
    .endOf('day');

  const startDate = time
    .clone()
    .subtract(dateRangeOffset, 'days')
    .subtract(dateRange, 'days')
    .startOf('day');

  return {
    startDate,
    endDate,
  };
};

/**
 * When given a `comparisonType`, `dateRange`, and `dateOffset` get the
 * comparison `startDate` and `endDate`.
 */
export const getComparisonStartAndEndDates = (
  comparisonType: ComparisonType,
  dateRange: number,
  dateRangeOffset: number,
  time: Moment,
) => {
  const { startDate: primaryStartDate, endDate: primaryEndDate } = getStartAndEndDates(
    dateRange,
    dateRangeOffset,
    time,
  );

  if (comparisonType === ComparisonType.PERIOD || comparisonType === ComparisonType.NONE) {
    // For a period comparison we need to subtract the `dateRange` from the
    // start and end dates to get the correct comparison dates.
    const periodOffset = dateRange === 0 ? 1 : dateRange;
    const startDate = primaryStartDate.clone().subtract(periodOffset, 'days');
    const endDate = primaryEndDate.clone().subtract(periodOffset, 'days');

    return { startDate, endDate };
  } else if (comparisonType === ComparisonType.YEAR) {
    // For a year comparison we need to subtract a year from the start and end
    // dates to get the correct comparison dates.
    const startDate = primaryStartDate.clone().subtract(1, 'year');
    const endDate = primaryEndDate.clone().subtract(1, 'year');

    return { startDate, endDate };
  }

  // As a fallback we just return the current dates
  return {
    startDate: primaryStartDate,
    endDate: primaryEndDate,
  };
};

export const getTimeAsTimestamp = (time: Moment): string => {
  return time.format();
};
